import type { Alert, AlertStatus, AlertType } from "@/features/alert/types";
import type { UserRole } from "@/features/user/types";

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  change_request: "Alteração",
  product_request: "Produto",
  checker_manual: "Manual",
  checkin_mismatch: "Divergência",
  box_threshold: "Saldo caixa",
  return_decision: "Devolução",
  report_mensal_due: "Mensal",
  report_pedido_due: "Pedido",
  report_pedido_stale: "Reenvio",
  supplier_price_list_due: "Lista de preços",
};

export type AlertPopupSection = "actionable" | "new";

export type AlertDestination =
  | { to: "/pedidos"; search?: { orderId: string } }
  | { to: "/cargas/$checkinId"; params: { checkinId: string } }
  | { to: "/relatorios" }
  | { to: "/mais/fornecedores" }
  | {
      to: "/mais/produtos";
      search: { reviewRequestId: string; reviewMode: "create" };
    }
  | { to: "/lista"; search: { date?: string } };

type AlertCapabilities = {
  canManageOrders: boolean;
  canManageCheckins: boolean;
};

export type AlertActionMode =
  | "super-product-request"
  | "supplier-price-list-due"
  | "generic";

export function getAlertTypeLabel(alertType: AlertType) {
  return ALERT_TYPE_LABELS[alertType];
}

export function getAlertPopupSection(alert: Alert): AlertPopupSection | null {
  if (alert.status === "solved") return null;
  if (alert.actionable) return "actionable";
  return alert.status === "sent" ? "new" : null;
}

export function getAlertTraySections(alerts: Alert[]) {
  // Backend interleaves tray categories; popup displays at most 3 actions and 4 news.
  const actionable: Alert[] = [];
  const newAlerts: Alert[] = [];

  for (const alert of alerts) {
    const section = getAlertPopupSection(alert);
    if (section === "actionable" && actionable.length < 3) {
      actionable.push(alert);
    } else if (section === "new" && newAlerts.length < 4) {
      newAlerts.push(alert);
    }
  }

  return { actionable, newAlerts };
}

export function getNotificationBellLabel(count: number | undefined) {
  if (count === undefined) return "Notificações";
  if (count === 0) return "Nenhuma notificação exige atenção";
  if (count === 1) return "1 notificação exige atenção";
  return `${count} notificações exigem atenção`;
}

export function getAlertPreviewText(alert: Alert) {
  const [title = "Notificação", ...details] = alert.message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  let context = details.join(" · ");
  if (!context && alert.target_order_id) context = "Pedido relacionado";
  if (!context && alert.checkin_id) context = "Carga relacionada";
  if (!context && alert.target_supplier_id) context = "Fornecedor relacionado";
  if (!context) context = "Central de notificações";

  return { title, context };
}

export function getAlertActionVisibility(
  alert: Alert,
  role: UserRole | undefined,
  capabilities: AlertCapabilities,
) {
  const isActive = alert.status !== "solved";
  const showWorkflowActions = isActive && alert.actionable;
  const isReturnDecision =
    alert.alert_type === "return_decision" &&
    !!alert.checkin_id &&
    !!alert.checkin_item_id;

  return {
    showWorkflowActions,
    canMarkSeen: isActive && alert.status === "sent",
    canManageChangeRequest:
      showWorkflowActions &&
      alert.alert_type === "change_request" &&
      !!alert.change_request_id &&
      capabilities.canManageOrders,
    canReplaceReturn:
      showWorkflowActions && isReturnDecision && role === "super",
    canCancelReturn:
      showWorkflowActions && isReturnDecision && capabilities.canManageCheckins,
    canReviewProduct:
      showWorkflowActions &&
      alert.alert_type === "product_request" &&
      role === "super" &&
      !!alert.context_key,
    canOpenSupplierPriceList:
      showWorkflowActions &&
      alert.alert_type === "supplier_price_list_due" &&
      role === "supplier",
    canOpenReportWorkspace:
      isActive &&
      role === "super" &&
      (alert.alert_type === "report_mensal_due" ||
        alert.alert_type === "report_pedido_stale"),
    canEditNote: role === "super",
  };
}

export function getAlertDestination(
  alert: Alert,
  role: UserRole | undefined,
): AlertDestination | null {
  if (
    alert.actionable &&
    role === "super" &&
    alert.alert_type === "product_request" &&
    alert.context_key
  ) {
    return {
      to: "/mais/produtos",
      search: {
        reviewRequestId: alert.context_key,
        reviewMode: "create",
      },
    };
  }
  if (
    alert.actionable &&
    role === "supplier" &&
    alert.alert_type === "supplier_price_list_due"
  ) {
    return {
      to: "/lista",
      search: { date: getSupplierPriceListDueDate(alert.context_key) },
    };
  }
  if (role === "super" && alert.alert_type === "report_mensal_due") {
    return { to: "/mais/fornecedores" };
  }
  if (role === "super" && alert.alert_type === "report_pedido_stale") {
    return { to: "/relatorios" };
  }
  if (alert.alert_type === "report_pedido_due") {
    return { to: "/relatorios" };
  }
  if (
    alert.actionable &&
    alert.alert_type === "return_decision" &&
    alert.checkin_id
  ) {
    return {
      to: "/cargas/$checkinId",
      params: { checkinId: alert.checkin_id },
    };
  }
  if (alert.actionable && alert.alert_type === "change_request") {
    return alert.target_order_id
      ? { to: "/pedidos", search: { orderId: alert.target_order_id } }
      : { to: "/pedidos" };
  }
  return null;
}

export function getAlertActionMode(
  alert: Alert,
  role: UserRole | undefined,
): AlertActionMode {
  if (!alert.actionable) return "generic";
  if (
    role === "super" &&
    alert.alert_type === "product_request" &&
    alert.context_key
  ) {
    return "super-product-request";
  }
  if (role === "supplier" && alert.alert_type === "supplier_price_list_due") {
    return "supplier-price-list-due";
  }
  return "generic";
}

export function getSeenTransitionStatus(): AlertStatus {
  return "seen";
}

export function getSupplierPriceListDueDate(
  contextKey?: string | null,
): string | undefined {
  const match = contextKey?.match(
    /^supplier_price_list_due:[^:]+:(\d{4}-\d{2}-\d{2})$/i,
  );
  return match?.[1];
}
